import React from 'react'
import seed from 'seed-random'
import moment from 'moment'
import mtz from 'moment-timezone'
import { utcToZonedTime } from 'date-fns-tz'
import {
  formatDistanceToNowStrict,
  startOfWeek,
  startOfYesterday,
  startOfToday,
  endOfToday,
  endOfTomorrow,
  endOfWeek,
  isAfter,
  isBefore,
  add as dateAdd,
  differenceInSeconds,
  sub as dateSub,
  differenceInCalendarDays,
  startOfDay,
  isEqual,
  getYear,
  format as dateFormat,
  formatDistanceStrict
} from 'date-fns'

import { formatDateTask } from '../timeUtilities'
export * from './apiUtilities'

// Gets the given time as `XX:XX AM/PM` format
export const formatTimeStamp = time => {
  const date = new Date(time)

  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  })
}

// Converts note/message from api to our format
export const hydrateMessage = msg => {
  const {
    content,
    tags,
    action_type,
    id,
    stamp,
    sessionId: session_id,
    action
  } = msg

  const date = new Date(stamp)
  const formatted = {
    content: content.body,
    media: content.media,
    senderFull: content.user,
    meta: content.meta,
    session_id,
    sender: content.user?.name ?? '',
    type: action_type,
    tags,
    id,
    date,
    action
  }

  if (content.title) {
    formatted.title = content.title
  }

  return formatted
}

// Converts a timeline object from the API into a list of messages that the system uses
export const timelineToMessages = timeline => {
  const messages = timeline.filter(
    ({ action_type, action }) =>
      ['chat', 'sms', 'join', 'email', 'part', 'add', 'kick'].includes(
        action_type
      ) || ['survey', 'session'].includes(action)
  )

  const hydrated = messages.map(hydrateMessage)

  return hydrated
}

// Converts a timeline object from the API into a list of notes that the system uses
export const timelineToNotes = timeline => {
  const notes = timeline.filter(({ action_type }) => action_type === 'note')

  const hydrated = notes.map(hydrateMessage)

  return hydrated
}

// Moves a value in an array from the old index to the new one
export const shiftValue = (array, oldIdx, newIdx) => {
  const copy = [...array]

  if (array.length < 2) {
    return copy
  }

  const oldIn = oldIdx >= 0 && oldIdx < array.length
  const newIn = newIdx >= 0 && newIdx < array.length

  if (!oldIn || !newIn) {
    return copy
  }

  const oldVal = copy.splice(oldIdx, 1)[0]
  copy.splice(newIdx, 0, oldVal)
  return copy
}

// Dowloads from a URL
export const downloadURL = (name, url) => {
  const el = document.createElement('a')
  el.href = url
  el.download = name
  document.body.appendChild(el)
  el.click()
}

// Downloads a blob
export const downloadBlob = (name, blob) => {
  const url = URL.createObjectURL(blob)
  downloadURL(name, url)
}

// Removes extra characters from a phone number
export const stripPhone = number => {
  const toReplace = ['-', '+1', '(', ')', ' ', '+', '.']
  let newNumber = number
  if (number) {
    toReplace.forEach(val => {
      while (newNumber.includes(val)) {
        newNumber = newNumber.replace(val, '')
      }
    })
    return newNumber
  }
  return ''
}

// Formats a phone number in the expected format
export const formatPhoneforAPI = number => {
  const stripped = stripPhone(number)
  const a = stripped.slice(0, 3)
  const b = stripped.slice(3, 6)
  const c = stripped.slice(6)
  return `+1-${a}-${b}-${c}`
}

// Replaces ids with names, update sw.js to use the same one
export const replaceMentions = (body, agents = [], keepAt = true) => {
  if (!body || typeof body !== 'string') {
    return ''
  }
  let fixed = body
  agents.forEach(({ id, name }) => {
    const toReplace = `|@${id}|`
    const replacement = `${keepAt ? '@' : ''}${name}`
    while (fixed.includes(toReplace)) {
      fixed = fixed.replace(toReplace, replacement)
    }
  })

  // Add loading to remaining tags
  fixed = fixed.replace(
    /\|@[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\|/gm,
    'Loading...'
  )

  return fixed
}

// Tests if a mention exists in a string
export const hasMention = message => {
  const mentionRE =
    /\|@[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\|/

  return mentionRE.test(message)
}

// Separates tags from message content
export const parseTags = message => {
  // Expressions that match tags
  const tagRE = /(#\w+)/gm

  const tags = [...message.matchAll(tagRE)].map(match => match[0].slice(1))
  const body = message.replace(tagRE, '')

  return [body, uniqueArray(tags)]
}

// Gets a random value from a list
const pickFromList = (list, rand = Math.random) => {
  const idx = Math.floor(rand() * list.length)
  return list[idx]
}

// Picks a random value from a list, using seedVal as a seed for the randomness
export const pickFromListSeeded = (list, seedVal) => {
  const rand = seed(seedVal)
  return pickFromList(list, rand)
}

// Gets the abbreviation for the local time zone
export const getTimeZoneAbbreviation = () => {
  return new Date()
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2]
}

// Gets url from a blob, used after getting media from API
export const getURLFromBlob = blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      resolve(reader.result)
    })

    reader.readAsDataURL(blob)
  })
}

// Gets a date string in relative terms (Today, Tomorrow, X Days)
export const formatDateRelative = (date, forTask = false) => {
  const realDate = moment(date).toDate()
  let now = new Date()
  if (forTask) {
    now = moment(now).startOf('D').toDate()
  }

  if (isBefore(realDate, startOfToday())) {
    if (isAfter(realDate, startOfYesterday())) {
      return 'Yesterday'
    }

    if (isAfter(realDate, startOfWeek(now))) {
      const dayGap = formatDistanceStrict(realDate, now, {
        addSuffix: false,
        unit: "day",
      });
      const [days, word] = dayGap.split(' ')
      return `${days} ${toTitleCase(word)} ago`
    }

    if (forTask) {
      return formatDateTask(realDate)
    }

    return moment(realDate).format('MMMM Do')
  }

  if (isBefore(realDate, endOfToday())) {
    return 'Today'
  }

  if (isBefore(realDate, endOfTomorrow())) {
    return 'Tomorrow'
  }

  // X Days if date is in the week
  if (isBefore(realDate, endOfWeek(now))) {
    const dayGap = formatDistanceStrict(realDate, now, {
      addSuffix: false,
      unit: "day",
    });
    const [days, word] = dayGap.split(' ')
    if (days === '1') {
      return 'Tomorrow'
    }
    return `${days} ${toTitleCase(word)}`
  }

  // Month and day
  if (forTask) {
    return formatDateTask(realDate)
  }

  return moment(realDate).format('MMMM Do')
}

// Gets relative time to a date, -1 if passed, 0 if soon, 1 if further away
export const getDateDistance = date => {
  const now = new Date()
  const due = moment(date).toDate()

  if (isBefore(due, now)) {
    return -1
  }

  const in2Days = dateAdd(now, { days: 2 })
  if (isBefore(due, in2Days)) {
    return 0
  }

  return 1
}

// Checks if 2 arrays have the same elements
export const arraysEqual = (a = [], b = [], preserveOrder = false) => {
  if (a.length !== b.length) {
    return false
  }

  const aCopy = a.slice()
  const bCopy = b.slice()

  if (!preserveOrder) {
    aCopy.sort()
    bCopy.sort()
  }

  for (let i = 0; i < a.length; i++) {
    if (aCopy[i] !== bCopy[i]) {
      return false
    }
  }

  return true
}

// Gets elements that exist in a but not b
export const arrayDifference = (a = [], b = []) => {
  const intersect = a.filter(item => !b.includes(item))
  return intersect
}

// Gets a distance from now in shortened form
// 10 seconds => 10s
export const getShortenedDistance = date => {
  try {
    const map = {
      seconds: 's',
      minutes: 'm',
      hours: 'h',
      days: 'd',
      months: 'mo',
      years: 'y'
    }

    const realDate = new Date(date)
    let dist = formatDistanceToNowStrict(realDate)

    Object.entries(map).forEach(([key, val]) => {
      dist = dist
        .replace(` ${key}`, val)
        .replace(` ${key.slice(0, key.length - 1)}`, val)
    })

    return `${dist}`
  } catch (err) {
    return ''
  }
}

// Removes prefix from the
export const stripPrefix = (val, prefix) => {
  if (!prefix) {
    return val
  }

  if (!val.startsWith(prefix)) {
    return val
  }

  return val.slice(prefix.length)
}

export const getMessageFromError = (
  error,
  DEFAULT_MESSAGE = 'An error occured, please try again.'
) => {
  if (!error) {
    return DEFAULT_MESSAGE
  }
  let response = error.err?.response
  let axiosError = error.err?.axiosError
  if (!axiosError) {
    axiosError = error?.axiosError
  }
  const errorResponse = axiosError?.response ?? response

  let message
  if (errorResponse) {
    message = errorResponse.data?.error_message
  }

  return message || error?.err?.message || error?.message || DEFAULT_MESSAGE
}

// Removes non numeric characters from a phone number
export const stripNonPhoneCharacters = str => {
  if (typeof str !== 'string') {
    return ''
  }

  let stripped = ''

  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    if (char >= '0' && char <= '9') {
      stripped += char
    }
  }

  return stripped
}

// Returns a number as e164 format if possible
// Return type is [error, e164 number]
export const checkE164 = toCheck => {
  if (typeof toCheck !== 'string') {
    return ['Please enter a phone number', null]
  }

  const E164_REGEX = /^\+[1-9]\d{10,14}$/
  const startingNumber = stripNonPhoneCharacters(toCheck)
  const toPrepend = ['+', '+1']

  for (const pre of toPrepend) {
    const newNumber = `${pre}${startingNumber}`
    const matches = newNumber.match(E164_REGEX)

    if (matches) {
      return [null, newNumber]
    }
  }

  return ['Numbers must include area and country codes', null]
}

// Performs channel formatting based on type
export const formatChannelValue = (type, val) => {
  if (type === 'email') {
    return val
  }

  const [error, number] = checkE164(val)
  if (error) {
    return stripNonPhoneCharacters(val)
  }

  return number
}

// Gets messages and notes in the current session
export const filterBySession = ({
  session = null,
  messages = [],
  notes = []
}) => {
  const fMessages = messages.filter(msg => msg.session_id === session)
  const fnotes = notes.filter(note => note.session_id === session)
  return { messages: fMessages, notes: fnotes }
}

// Gets the latest event from a conversation/dm object
export const getLatestEvent = (obj, session, reverse = false) => {
  if (!obj) {
    return null
  }

  // Latest should be an array
  let latest = []
  if (obj.latestActivity) {
    if (Array.isArray(obj.latestActivity)) {
      latest = obj.latestActivity
    } else {
      latest = [obj.latestActivity]
    }
  }

  // Combine event sources
  const { messages, notes } = filterBySession({ ...obj, session })
  const combined = [...messages, ...notes, ...latest]

  // Null when no events
  if (!combined.length) {
    return { date: obj.startDate }
  }

  // Sort so newest is first, unless reversed
  const sorted = combined.sort((a, b) => {
    const first = reverse ? a : b
    const second = reverse ? b : a
    return new Date(first.date) - new Date(second.date)
  })

  // Newest or oldest event
  return sorted[0]
}

// Gets the latest event from a conversation/dm object
export const getAppAlertLatestEvent = (obj, session, reverse = false) => {
  if (!obj) {
    return null
  }

  // Latest should be an array
  let latest = []
  if (obj.latest_activity) {
    if (Array.isArray(obj.latest_activity)) {
      latest = obj.latest_activity
    } else {
      latest = [obj.latest_activity]
    }
  }

  // Combine event sources
  const { messages, notes } = filterBySession({ ...obj, session })
  const combined = [...messages, ...notes, ...latest].filter(
    x => x.action === 'chat'
  )

  // Null when no events
  if (!combined.length) {
    return { date: obj.startDate }
  }

  // Sort so newest is first, unless reversed
  const sorted = combined.sort((a, b) => {
    const first = reverse ? a : b
    const second = reverse ? b : a
    return new Date(first.date) - new Date(second.date)
  })

  // Newest or oldest event
  return sorted[0]
}
// Figures out what id to use for filtering
export const findFilterID = (owner_id, isGroup, session) => {
  // Regular conversations and group chats
  if (owner_id || isGroup) {
    return session
  }

  // For peer chats
  return null
}

/**
 * Counts the number of unread events
 * @param {string} session The id of this session/dm
 * @param {any} convos The convos object from the read status provider
 * @param {string} myID The current user id
 * @param {any[]} events The events to check in (notes/messages)
 * @returns The unread count
 */
export const countUnread = (session, convos, myID, events) => {
  if (!session || !(session in convos)) {
    return 0
  }

  if (!myID) {
    return 0
  }

  const loadedDate = sessionStorage.getItem('loaded date')
  const yourLast = convos[session][myID] ?? new Date(loadedDate)

  if (!yourLast) {
    return 0
  }

  const lastDate = new Date(yourLast)

  let count = 0
  // Only check messages that are not yours
  const chats = events.filter(
    msg => msg.type === 'chat' && msg.senderFull?.id !== myID
  )
  chats.forEach(msg => {
    const valid = msg.session_id ? msg.session_id === session : true

    if (valid && lastDate < new Date(msg.date)) {
      count++
    }
  })

  return count
}

/**
 * Converts string to title case
 * @example 'test' -> 'Test'
 * @param {string} str - String to convert to title case
 */
export const toTitleCase = str => {
  return str[0].toLocaleUpperCase() + str.slice(1)
}

/**
 * Replaces instances of oldKey with newKey in the object
 * @param {object} obj - Object to change keys of
 * @param {string} oldKey - Old key to replace
 * @param {string} newKey - New key to use
 * @param {boolean?} recursive - Whether replacement should happen on sub object keys
 */
export const replaceKeys = (obj, oldKey, newKey, recursive = false) => {
  const newObj = {}

  Object.entries(obj).forEach(([key, val]) => {
    const id = key === oldKey ? newKey : key
    let newVal = val
    if (recursive && typeof newVal === 'object') {
      newVal = replaceKeys(val, oldKey, newKey, true)
    }
    newObj[id] = newVal
  })

  return newObj
}

export const checkOnDifferentDays = (a, b) => {
  if (!a || !b) {
    return false
  }

  const diff = differenceInCalendarDays(a.date, b.date)
  return Math.abs(diff) > 0
}

export const getDayDividerDate = (date, now = new Date()) => {
  const asDate = new Date(date)

  const yesterday = dateSub(now, { days: 1 })

  if (isEqual(startOfDay(date), startOfDay(now))) {
    return 'Today'
  }

  if (isBefore(asDate, startOfDay(now))) {
    if (isAfter(asDate, startOfDay(yesterday))) {
      return 'Yesterday'
    }
  }

  let format = 'MMM d'
  if (getYear(asDate) !== getYear(now)) {
    format = 'MMM d, yyyy'
  }

  return dateFormat(asDate, format)
}

// above check group
export const checkInGroup = (a, b) => {
  if (!a || !b) {
    return false
  }

  // Don't connect accross sessions
  if (a.session_id !== b.session_id) {
    return false
  }

  // if message not same sender, return false
  if (a.sender !== b.sender) {
    return false
  }

  // if not the same channel, return false
  if (a.type !== b.type) {
    return false
  }

  // dont connect join messages
  if (a.type === 'add' || b.type === 'add') {
    return false
  }

  // dont count duplicates.
  if (a && b && a.id === b.id) {
    return false
  }

  if (a.meta?.side_channels && b.meta?.side_channels) {
    const aList = a.meta.side_channels.map(c => c.id).filter(Boolean)
    const bList = b.meta.side_channels.map(c => c.id).filter(Boolean)

    if (!arraysEqual(aList, bList)) {
      return false
    }
  }

  const diff = Math.abs(differenceInSeconds(a.date, b.date))
  const diff2 = Math.abs(differenceInSeconds(a.date?._d, b.date?._d))
  if (diff <= 30 || diff2 <= 30) {
    return true
  }

  return false
}

// Converts array of objects to object
export const arrayToObject = arr => {
  const obj = arr.reduce((prev, curr) => {
    prev[curr.id] = curr
    return prev
  }, {})

  return obj
}

// Comes from Conversation Content component
export const formatLastMessage = (
  lastMessage,
  allAgents,
  allUsers,
  currentUser
) => {
  if (!lastMessage) {
    return <>&zwnj;</>
  }

  const { content, senderFull, sender, type, meta, media } = lastMessage
  const isMine = senderFull?.id === currentUser()?.id

  if (!senderFull?.id) {
    return <>&zwnj;</>
  }

  if (content) {
    const toCheck = [...allAgents, ...Object.values(allUsers)]
    if (Array.isArray(content)) {
      return replaceMentions(content[0]?.text, toCheck)
    }
    const senderName = sender
    const userName = allUsers[senderFull.id]?.name
    const name = userName && userName !== senderName ? userName : senderName

    const cleanedText = replaceMentions(content, toCheck)
    return `${isMine ? 'Me' : name}: ${cleanedText}`
  }

  if (['join', 'part'].includes(type)) {
    const name = sender
    const isPart = type === 'part'
    return `${name} ${isPart ? 'left' : 'joined'}.`
  }

  if (['add', 'kick'].includes(type)) {
    const name = sender
    const name2 = allUsers[meta?.by_user]?.name
    if (!name2) {
      return 'Loading...'
    }
    const isAdd = type === 'add'
    if (name === name2) {
      return `${name} ${isAdd ? 'joined' : 'left'}.`
    }

    return `${name} was ${isAdd ? 'added' : 'removed'} by ${name2}.`
  }

  if (type === 'assigned') {
    const name = sender
    return `Accepted by ${name}.`
  }

  if (media?.length > 0) {
    const mediaType = media[0].type
    return `${isMine ? 'Me' : sender}: <${mediaType}>`
  }

  return <>&zwnj;</>
}

// Comes from Conversation Content component
export const formatLastMessageForAppNotification = (
  lastMessage,
  allAgents,
  allUsers
) => {
  if (!lastMessage) {
    return null
  }

  const { content } = lastMessage

  if (content) {
    const toCheck = [...allAgents, ...Object.values(allUsers)]
    if (Array.isArray(content)) {
      return replaceMentions(content[0]?.text, toCheck)
    }

    const cleanedText = replaceMentions(content, toCheck)
    return `${cleanedText}`
  }

  return null
}

// Splits an array into chunks of size, returns array of arrays
export const chunkArray = (arr, size) => {
  if (size <= 0) {
    return [arr]
  }

  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }

  return chunks
}

export const checkMention = (content, className, users, you, classNameOwn) => {
  const mentionsRE = /\B@\w+/g
  const check = [...content.matchAll(mentionsRE)].map(match => {
    return match
  })

  let replace = ''
  content.split(' ').forEach((s, i) => {
    if (s.match(mentionsRE)) {
      let style = className
      const name = replaceMentions(s, Object.values(users))

      if (name.substring(1) === you) {
        style = classNameOwn
      }
      return (replace += `<span class=${style}>${name}</span>`)
    } else {
      replace += `${s} `
    }
  })

  return check.length ? replace : false
}

export const getInitials = (name, maxInitials = 2) => {
  if (!name) {
    return ''
  }

  if (typeof name === 'number') {
    return `+${name}`
  }

  if (typeof name !== 'string') {
    return ''
  }

  const names = name.toUpperCase().split('')
  const initials = names.map(name => {
    let initial = ''
    try {
      initial = String.fromCodePoint(name.codePointAt(0))
    } catch (err) {}
    return initial
  })
  return initials.slice(0, maxInitials).join('')
}

// formats date to MM/DD/YYYY
export const formatDate_MMDDYYYY = iso => {
  let edited = new Date(iso)

  return `${edited.getMonth() + 1}/${edited.getDate()}/${edited.getFullYear()}`
}

// masks api key
export const maskKey = key => {
  if (key.length <= 8) {
    return key
  }

  const masked = key.replace(/^.{1,32}/, mask => '*'.repeat(mask.length))
  const newKey = masked.substr(masked.length - 8)

  return newKey
}

export const formatMessageTooltipDate = date => {
  const format = 'EEE, MMM d yyyy h:mm aaa'

  try {
    const asDate = new Date(date)
    return dateFormat(asDate, format)
  } catch {
    console.warn('Invalid date for formatMessageTooltipDate', date)
    return dateFormat(Date.now(), format)
  }
}

export const uniqueArray = (...args) => {
  return [...new Set(args.flat(Infinity))]
}

export const isUUID = str => {
  const uuidRE =
    /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/
  return uuidRE.test(str)
}

export const loadTaggedUsers = (message, loadUser) => {
  const re =
    // eslint-disable-next-line
    /\|\@[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\|/gm

  const matches = uniqueArray([...message.matchAll(re)])
  const ids = matches.map(m => m.slice(2, -1))

  ids.map(loadUser)
}

export const getReasonWarningText = (newVal, oldVal) => {
  return newVal?.external && !oldVal?.external
    ? `You're changing a system user into a contact type. Their properties and behaviour in the system will change.`
    : `You're changing a contact into a system user. Their properties and behaviour in the system will change.`
}

export const convertToTimezone = (timezone, date, current) => {
  const zone = timezone || current
  const asDate = new Date(date ?? Date.now())
  return utcToZonedTime(asDate, zone)
}

export const getShortCode = (timezone, current) => {
  const zone = timezone || current
  return mtz.tz(zone).zoneAbbr()
}

export const formatSessionForPut = conversation => {
  const {
    session,
    name,
    topic,
    state,
    profile,
    owner_id,
    tags,
    users,
    inbox_id,
    // ended_at,
    closed_at,
    assigned_to
  } = conversation

  return {
    id: session,
    name: name ?? null,
    topic: topic ?? null,
    state: state ?? 'ACTIVE',
    profile: profile ?? 'PUBLIC_CLOSED',
    owner_id: owner_id ?? null,
    assigned_to: assigned_to ?? null,
    tags: tags ?? [],
    userIds: users,
    // ended_at: ended_at ?? null,
    // closed_at: closed_at ?? null,
    // TODO: Remove later
    closedAt: closed_at ?? null,
    inbox_id
  }
}

export const sortByName = (a, b) => {
  if (a?.name?.toLowerCase() < b.name?.toLowerCase()) {
    return -1
  }
  if (a?.name?.toLowerCase() > b.name?.toLowerCase()) {
    return 1
  }
  return 0
}

export const sortByRenderedComponentName = (a, b) => {
  if (a?.props?.opt?.name?.toLowerCase() < b?.props?.opt?.name?.toLowerCase()) {
    return -1
  }
  if (a?.props?.opt?.name?.toLowerCase() > b?.props?.opt?.name?.toLowerCase()) {
    return 1
  }
  return 0
}

export const nullExpandArray = (arr, amount = 1) => {
  const newArr = []

  const pad = () => {
    for (let i = 0; i < amount; i++) {
      newArr.push(null)
    }
  }

  pad()
  arr.forEach(val => {
    newArr.push(val)
    pad()
  })

  return newArr
}

export const moveItem = (
  array,
  source,
  target,
  above,
  getIDFunc = id => id
) => {
  // Add gaps to the array
  const expanded = nullExpandArray(array)

  // Find indices of both items in newly expanded array
  const sourceIdx = expanded.findIndex(
    item => (item ? getIDFunc(item) : null) === source
  )
  const targetIdx = expanded.findIndex(
    item => (item ? getIDFunc(item) : null) === target
  )

  // Perform move
  expanded[targetIdx + (above ? -1 : 1)] = expanded[sourceIdx]
  expanded[sourceIdx] = null

  // Remove gaps after change
  const moved = expanded.filter(Boolean)

  return moved
}
